import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { getAllFileTypes } from '@app/functions/files/get-all-file-types';
import { getImageFileTypes } from '@app/functions/files/get-image-file-types';
import { File } from '@app/models/common/file.model';
import { Employee } from '@app/models/employee/employee.model';
import { TaskSubtask } from '@app/models/tasks/task-subtask.model';
import { Task } from '@app/models/tasks/task.model';
import {
    AnalyticService,
    AuthService,
    FileHelperService,
    NotifyService,
    TasksService,
    TaskSubtasksService,
} from '@app/services';

@Component({
    selector: 'app-task-details',
    templateUrl: './task-details.template.html',
    styleUrls: ['./task-details.style.scss'],
})
export class TaskDetailsComponent {
    @Output() close: EventEmitter<void> = new EventEmitter<void>();
    @Output() toggleComplete: EventEmitter<void> = new EventEmitter<void>();

    @Input() task: Task;
    @Input() employee: Employee;

    isLoading = false;

    isAdmin = false;

    progressBarValue = 0;

    allowedImgTypes = getImageFileTypes();
    allowedFileTypes = getAllFileTypes();

    showFileAttachmentError = false;

    completedSubtasksCount = 0;
    totalSubtasksCount = 0;

    isComplete = false;

    constructor(
        private tasksService: TasksService,
        private taskSubtasksService: TaskSubtasksService,
        private auth: AuthService,
        protected element: ElementRef,
        private notify: NotifyService,
        private router: Router,
        public analyticService: AnalyticService,
        private fileHelper: FileHelperService
    ) {
        this.isAdmin = this.auth.isAdmin();
    }

    beforeShow(task: Task) {
        this.showFileAttachmentError = false;

        this.isComplete = task.isComplete();
        this.setProgressBarValue(task);
    }

    async viewFile(file: File): Promise<void> {
        try {
            this.fileHelper.saveFile(file);
        } catch (e) {
            this.notify.error('employees.task-details.unableToDownload');
        }
    }

    async onToggleSubTaskComplete(currentSubtask: TaskSubtask): Promise<void> {
        // set pendo events
        if (currentSubtask.completedAt) {
            //Sub task iscomplete, user is now marking it as incomplete
            this.analyticService.trackEvent(AnalyticEvents.MarkSubtaskAsIncompleteInTaskDetailsModal);
        } else {
            this.analyticService.trackEvent(AnalyticEvents.MarkSubtaskAsCompleteInTaskDetailsModal);
        }

        // quick ui update, overridden by api call but avoids slow ux
        currentSubtask.completedAt = currentSubtask.completedAt ? null : Date.now() / 1000;

        try {
            const subTask = await this.taskSubtasksService.toggleCompletion(this.task, currentSubtask);

            this.task.subtasks.splice(this.task.subtasks.indexOf(currentSubtask), 1, subTask);
            this.setProgressBarValue(this.task);
        } catch (e) {
            this.notify.error('employees.task-details.unableToCompleteSubtask');
        }
    }

    async onToggleTaskComplete(): Promise<void> {
        // If a file attachment is required but there's no file, show error
        if (!this.task.isComplete() && this.task.isAttachmentRequired && !this.task.fileId) {
            this.showFileAttachmentError = true;
            this.notify.error('employees.task-details.unableToCompleteTaskAttachmentRequired');
            return;
        }

        try {
            await this.tasksService.toggleCompletion(this.task);

            // send a event back to parent
            this.toggleComplete.emit();

            // set pendo events
            if (this.task.completedAt) {
                this.analyticService.trackEvent(AnalyticEvents.MarkTaskAsCompleteInTaskDetailsModal);
            } else {
                this.analyticService.trackEvent(AnalyticEvents.MarkTaskAsIncompleteInTaskDetailsModal);
            }
        } catch (e) {
            this.notify.error('employees.task-details.unableToCompleteTask');
        } finally {
            this.isComplete = !this.isComplete;
        }
    }

    onClose(): void {
        this.close.emit();
    }

    async sendReminder(): Promise<void> {
        this.isLoading = true;

        try {
            await this.task.sendReminder();

            this.analyticService.trackEvent(AnalyticEvents.RemindToCompleteInTaskDetailsModal);
            this.notify.success('employees.task-details.reminderSent');
        } catch (e) {
            this.notify.error('employees.task-details.unableToSendReminder');
        } finally {
            this.isLoading = false;
        }
    }

    onEdit(): void {
        this.analyticService.trackEvent(AnalyticEvents.EditEventInTaskDetailsModal);
        this.onClose();
        this.router.navigate(['/employees', 'directory', this.employee.id, 'tasks', this.task.id, 'edit']);
    }

    onViewTemplate(): void {
        this.analyticService.trackEvent(AnalyticEvents.ViewTemplateInTaskDetailsModal);
        this.onClose();
        this.router.navigate(['/employees', 'task-templates', this.task.taskTemplateId]);
    }

    onFileSelected(files: FileList, task: Task): void {
        this.isLoading = true;
        this.tasksService.attachFile(files, task).then(() => {
            this.isLoading = false;
            this.showFileAttachmentError = false;
        });
    }

    onFileRemoved(task: Task): void {
        this.isLoading = true;

        if (task.isComplete()) {
            task.completedAt = null;
        }

        this.tasksService.removeFile(task).then(() => {
            this.isLoading = false;
            this.showFileAttachmentError = false;
        });
    }

    setProgressBarValue(task: Task): void {
        this.completedSubtasksCount = task.subtasks.filter((subTask: TaskSubtask) => subTask.completedAt).length;
        this.totalSubtasksCount = task.subtasks.length;

        this.progressBarValue = (this.completedSubtasksCount / this.totalSubtasksCount) * 100;
    }
}
